import React, { useEffect, useState } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import { useDispatch } from 'react-redux';
import { useActor } from '@xstate/react';
import { CrmIntegrationType, MeetingStatus } from '@alucio/aws-beacon-amplify/src/models';
import {
  DNABox,
  DNAButton,
  DNADivider,
  DNAIcon,
  DNAText,
  Iffy,
  Popover,
} from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/lib/theming/themes/alucio/colors';
import { CommonIcon, DNAChip, DNACommonConfirmation, DNAPopover } from 'src/components/DNA';
import { MEETING_SUBMIT_TYPE, useAddMeeting, useIsMeetingLocked } from './AddMeetingProvider';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { useCRMStatus } from 'src/hooks/useCRMStatus';
import { CRMConnectionStatus } from 'src/state/machines/CRM/crmMachineTypes';
import { S } from 'src/screens/Publishers/Versioning/VersioningPanel/Header';
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal';
import { useAppSettings } from 'src/state/context/AppSettings';
import { MeetingORM } from 'src/types/orms';
import { usePermissions } from 'src/state/redux/selector/permissions';
import { useUserTenant } from 'src/state/redux/selector/user';
import { SyncMetaType, useSubmitButtonStatus } from './Util';
import { CRMUtil } from 'src/state/machines/CRM/util';

export const styles = StyleSheet.create({
  content: {
    backgroundColor: colors['color-text-white'],
    paddingHorizontal: 24,
    paddingVertical: 16,
  },
})

enum DEMO_SYNCING_STATUS {
  IDLE = 'Sync to CRM',
  SYNCING = 'Syncing...',
  SYNCED = 'Synced'
}

const SYNCING_BUTTON_META: SyncMetaType = {
  [DEMO_SYNCING_STATUS.IDLE]: {
    color: 'tertiary',
    icon: undefined,
  },
  [DEMO_SYNCING_STATUS.SYNCING]: {
    color: 'tertiary',
    icon: 'arrow-clockwise',
  },
  [DEMO_SYNCING_STATUS.SYNCED]: {
    color: 'success',
    icon: 'check',
  },
};

export const useCanDeleteMetting = (meetingORM: MeetingORM | undefined) => {
  const { isOnline } = useAppSettings();
  const { connectionStatus } = useCRMStatus();
  // The user is offline (as the request to the SF api to delete it can’t be sent)
  const deleteDisabledDueOffline = !isOnline && !!meetingORM?.model.crmRecord?.crmCallId
  // The user is not connected to CRM.
  const deleteDisabledDueCRMConnection = connectionStatus !== CRMConnectionStatus.CONNECTED &&
     !!meetingORM?.model.crmRecord?.crmCallId

  const [isDeletedDisabled, setDeleteDisabled] = useState<{
    deleteDisabled: boolean;
    deleteDisabledMessage: string;
  }>({
    deleteDisabled: false,
    deleteDisabledMessage: '',
  });

  useEffect(() => {
    if (deleteDisabledDueOffline) {
      setDeleteDisabled({
        deleteDisabled: true,
        deleteDisabledMessage: 'Please go online to delete the meeting.',
      });
    } else if (deleteDisabledDueCRMConnection) {
      setDeleteDisabled({
        deleteDisabled: true,
        deleteDisabledMessage: 'Please connect to CRM to delete the meeting.',
      });
    } else {
      setDeleteDisabled({
        deleteDisabled: false,
        deleteDisabledMessage: '',
      });
    }
  }, [deleteDisabledDueOffline, deleteDisabledDueCRMConnection]);

  return isDeletedDisabled;
}

export const SaveToBeaconButton = ({
  disabled = false,
  isSecondary = false,
}: {
  disabled?: boolean;
  isSecondary?: boolean;
}) => {
  const { onSave, isSubmitting } = useAddMeeting();
  const btnSubmitStatus = useSubmitButtonStatus();

  return (
    <DNAButton
      testID="meeting-save-button"
      appearance={isSecondary ? 'outline' : 'filled'}
      status={isSecondary ? 'tertiary' : btnSubmitStatus.color}
      iconLeft={btnSubmitStatus.icon}
      onPress={() => onSave()}
      disabled={disabled || isSubmitting || btnSubmitStatus.disabled}
    >
      {btnSubmitStatus?.text || 'Save'}
    </DNAButton>
  );
};

const AddMeetingFooter = () => {
  const {
    onCancel,
    onDelete,
    isSubmitting,
    meetingORM,
    isReadOnly,
  } = useAddMeeting()
  const dateLastSyncedAt = new Date(meetingORM?.model.crmRecord?.lastSyncedAt || '');
  const lastSyncedAt = (!isReadOnly ? 'Saved to CRM on ' : '') + dateLastSyncedAt.toLocaleDateString() + ' at ' +
    dateLastSyncedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const [demoSyncingStatus, setDemoSyncingStatus] = useState<DEMO_SYNCING_STATUS>(DEMO_SYNCING_STATUS.IDLE);
  const { canUseMeetingCRMFields, isAutomaticLoginEnabled, isCRMConfigEnabled } = useCRMStatus();
  const [uiDisabled, setUIDisabled] = useState<boolean>(false);
  const enableDemoMode = useFeatureFlags('enableDemoMode');
  const hideMeetingFormButtons = useFeatureFlags('BEAC_7919_hide_save_to_beacon_button');
  const isMeetingLocked = useIsMeetingLocked(meetingORM);
  const { deleteDisabled, deleteDisabledMessage } = useCanDeleteMetting(meetingORM);
  const shouldUsePrimaryCRMAction = hideMeetingFormButtons && isCRMConfigEnabled;

  const crmCallId = meetingORM?.model.crmRecord?.crmCallId;
  const openInCRMContent = crmCallId ? (
    <DNABox spacing="sm" alignY="center">
      <CRMOpenCallButton meetingORM={meetingORM} />
      <DNAChip.Beacon children={lastSyncedAt} />
    </DNABox>
  ) : null;

  const centerContent = hideMeetingFormButtons ? openInCRMContent : null;

  /** NOTE: Simulate sync delay */
  const onSyncDemo = (): void => {
    const syncStepDuration = 1500
    setDemoSyncingStatus(DEMO_SYNCING_STATUS.SYNCING)
    setTimeout(() => {
      setDemoSyncingStatus(DEMO_SYNCING_STATUS.SYNCED)
      setTimeout(() => {
        setDemoSyncingStatus(DEMO_SYNCING_STATUS.IDLE)
      }, syncStepDuration)
    }, syncStepDuration)
  }

  const primaryAction = enableDemoMode ? (
    <DNAButton
      appearance="filled"
      status={SYNCING_BUTTON_META[demoSyncingStatus].color}
      iconLeft={SYNCING_BUTTON_META[demoSyncingStatus].icon}
      onPress={onSyncDemo}
      disabled={uiDisabled}
    >
      {demoSyncingStatus}
    </DNAButton>
  ) : shouldUsePrimaryCRMAction ? (
    <CRMSubmitButton />
  ) : (
    <SaveToBeaconButton disabled={uiDisabled} />
  );

  const handleIsSubmitting = () => {
    setUIDisabled(isSubmitting || demoSyncingStatus !== DEMO_SYNCING_STATUS.IDLE)
  }
  useEffect(handleIsSubmitting, [isSubmitting, demoSyncingStatus])

  if (isReadOnly) { return (
    <DNABox fill appearance="col" alignY="end">
      <DNADivider />

      <DNABox spacing="sm" alignY="center" style={styles.content} >
        {/* Open in CRM */}
        <Iffy is={meetingORM?.model.crmRecord?.crmCallId && meetingORM?.model.crmRecord?.crmSyncStatus !== 'DELETED'}>
          <CRMOpenCallButton meetingORM={meetingORM} />
        </Iffy>
        <CommonIcon variant="lock" />
        <Iffy is={meetingORM?.model.crmRecord?.crmSyncStatus === 'DELETED' && meetingORM?.model.status === 'LOCKED'}>
          <DNAText status="subtle">
            This meeting is deleted on CRM and now is locked.
          </DNAText>
        </Iffy>
        <Iffy is={meetingORM?.model.status === 'LOCKED' && meetingORM?.model.crmRecord?.crmSyncStatus !== 'DELETED'}>
          <DNAText status="subtle">
            Submitted to CRM on {lastSyncedAt} and is now locked.
          </DNAText>
        </Iffy>
      </DNABox>

    </DNABox>
  ) }

  return (
    <DNABox fill appearance="col" alignY="end" >
      <DNADivider />
      <DNABox alignY="center" spacing={centerContent ? undefined : 'between'} style={styles.content}>
        {/* LEFT: Delete + secondary Save to Beacon (flag ON) + CRM dropdown/Open in CRM (flag OFF) */}
        <DNABox spacing="sm" alignY="center" fill={!!centerContent}>
          <Popover disablePopover={!deleteDisabledMessage}>
            <Popover.Anchor>
              <DNAButton
                appearance="outline"
                status="tertiary"
                iconRight="trash"
                onPress={onDelete}
                disabled={uiDisabled || isMeetingLocked || deleteDisabled || isSubmitting}
              />
            </Popover.Anchor>
            <Popover.Content>
              <DNAText colorMode="dark">{deleteDisabledMessage}</DNAText>
            </Popover.Content>
          </Popover>
          {shouldUsePrimaryCRMAction && (
            <SaveToBeaconButton disabled={uiDisabled} isSecondary />
          )}
          {!hideMeetingFormButtons && (canUseMeetingCRMFields || isAutomaticLoginEnabled) && <CRMSubmitButton />}
          {!hideMeetingFormButtons && openInCRMContent}
        </DNABox>
        {/* CENTER: Open in CRM (flag ON only, truly centered via equal-flex) */}
        {centerContent && (
          <DNABox alignX="center" alignY="center" fill>
            {centerContent}
          </DNABox>
        )}
        {/* RIGHT: Cancel + primary action (Demo mode > Save to CRM > Save) */}
        <DNABox spacing="sm" alignY="center" alignX={centerContent ? 'end' : undefined} fill={!!centerContent}>
          <DNAButton
            testID="meeting-cancel-button"
            appearance="outline"
            status="tertiary"
            onPress={onCancel}
            disabled={uiDisabled}
          >
            Cancel
          </DNAButton>
          {primaryAction}
        </DNABox>
      </DNABox>
    </DNABox>
  );
};

export const CRMSubmitButton = () => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const { isOnline, crmMachine } = useAppSettings();
  const permissions = usePermissions();
  const { crmIntegrationType, connectionStatus, hasCRMData, isAutomaticLoginEnabled } = useCRMStatus();
  const {
    meetingORM,
    onSave,
    isValid,
    meetingStatus,
    isSubmitting,
  } = useAddMeeting();
  const dispatch = useDispatch();
  const isMeetingLocked = useIsMeetingLocked(meetingORM)
  const canSubmitToCRM = !isMeetingLocked && isOnline && !isSubmitting;
  const isPlannedMeeting = meetingStatus === MeetingStatus.PLANNED;
  const isVeevaIntegration = crmIntegrationType === CrmIntegrationType.VEEVA ||
    crmIntegrationType === CrmIntegrationType.VEEVA_VAULT;
  const isConnectedToCRM = connectionStatus === CRMConnectionStatus.CONNECTED;
  const currentTenant = useUserTenant();
  const hideMeetingFormButtons = useFeatureFlags('BEAC_7919_hide_save_to_beacon_button');
  const [, send] = useActor(crmMachine);

  const onSyncToCRM = async (submitType?: MEETING_SUBMIT_TYPE): Promise<void> => {
    if (isConnectedToCRM) {
      onSave(submitType);
    } else {
      await onSave(MEETING_SUBMIT_TYPE.SILENT_SAVE, 'Please correct the error(s) before connecting to CRM.');
      if (isValid) {
        if (meetingORM?.model.id && submitType !== undefined) {
          CRMUtil.setPostAuthIntent({
            type: 'MEETING_SUBMIT',
            meetingId: meetingORM.model.id,
            submitType,
          });
        }
        send({
          type: 'LOGIN_CRM',
          payload: currentTenant?.config.crmIntegration,
          options: {
            trigger: submitType === MEETING_SUBMIT_TYPE.SUBMIT_LOCK_TO_CRM ? 'SUBMIT_TO_CRM' : 'SAVE_TO_CRM',
          },
        })
      }
    }
  };

  const onPressForCRM = (): void => {
    if (isConnectedToCRM && isVeevaIntegration && !isPlannedMeeting) {
      toggleVisibility();
    }
    else {
      onSyncToCRM(MEETING_SUBMIT_TYPE.SAVE_TO_CRM);
    }
  }

  const toggleVisibility = (): void => {
    setIsVisible((visible) => !visible);
  }

  const onSaveToCRM = (): void => {
    toggleVisibility();
    onSyncToCRM(MEETING_SUBMIT_TYPE.SAVE_TO_CRM);
  }

  const onFinalSubmitToCRM = (): void => {
    toggleVisibility();

    const onConfirm = (): void => {
      onSyncToCRM(MEETING_SUBMIT_TYPE.SUBMIT_LOCK_TO_CRM);
    };

    dispatch(
      DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: true,
        component: () => (
          <DNACommonConfirmation
            descriptionText="Once submitted, this meeting will no longer be editable."
            confirmActionText="Submit"
            title="Submit final meeting to CRM?"
            onConfirmAction={onConfirm}
          />
        ),
      }));
  }

  if (hideMeetingFormButtons) {
    const isDisabledDueToNoCRMData = !hasCRMData && !isAutomaticLoginEnabled;
    const isButtonDisabled = !canSubmitToCRM || isDisabledDueToNoCRMData;

    const getTooltipText = () => {
      if (isDisabledDueToNoCRMData) return 'Connect to CRM';
      if (!isOnline) return 'This feature is not available in offline mode.';
      if (isMeetingLocked) return 'This meeting has been submitted and locked in your CRM.';
      if (isSubmitting) return 'Saving to CRM...';
      return 'This meeting has been submitted and locked in your CRM.';
    };

    return (
      <DNAPopover visible={isButtonDisabled ? undefined : false}>
        <DNAPopover.Anchor>
          <DNAButton
            testID="submit-to-crm-button"
            appearance="filled"
            status="primary"
            onPress={() => onSyncToCRM(MEETING_SUBMIT_TYPE.SAVE_TO_CRM)}
            disabled={isButtonDisabled}
          >
            Save to CRM
          </DNAButton>
        </DNAPopover.Anchor>
        <DNAPopover.Content>
          <DNAText colorMode="dark">
            {getTooltipText()}
          </DNAText>
        </DNAPopover.Content>
      </DNAPopover>
    );
  }

  return (
    <DNAPopover visible={canSubmitToCRM ? false : undefined}>
      <DNAPopover.Anchor>
        <DNAPopover
          lazyMount
          placement="top-end"
          interactive={true}
          visible={isVisible}
          onBackdropPress={toggleVisibility}
          type="menu"
        >
          <DNAPopover.Anchor>
            <DNAButton
              testID="submit-to-crm-button"
              appearance="outline"
              status="tertiary"
              onPress={onPressForCRM}
              disabled={!canSubmitToCRM}
            >
              <DNABox spacing="sm" alignY="center">
                <DNAText status={canSubmitToCRM ? undefined : 'tertiary'}>
                  { isConnectedToCRM ? 'Sync to CRM' : 'Connect to CRM' }
                </DNAText>
                <Iffy is={isVeevaIntegration && !isPlannedMeeting}>
                  <DNAIcon.Styled
                    status={canSubmitToCRM ? 'tertiary' : 'subtle'}
                    name="caret-down"
                  />
                </Iffy>
              </DNABox>
            </DNAButton>
          </DNAPopover.Anchor>
          {/* CONTENT */}
          <DNAPopover.Content offset={5}>
            <DNABox
              style={S.headerActionsWrapper}
              appearance="col"
              spacing={permissions.enableCRMSubmit ? 'md' : undefined}
            >
              {/* SAVE IN CRM */}
              <Pressable onPress={onSaveToCRM}>
                <DNAText>
                  Save to CRM
                </DNAText>
              </Pressable>
              {/* SAVE IN CRM AND LOCK */}
              <Iffy is={permissions.enableCRMSubmit}>
                <Pressable onPress={onFinalSubmitToCRM}>
                  <DNAText>
                    Submit final to CRM
                  </DNAText>
                </Pressable>
              </Iffy>
            </DNABox>
          </DNAPopover.Content>
        </DNAPopover>
      </DNAPopover.Anchor>
      <DNAPopover.Content>
        <DNAText colorMode="dark">
          { isOnline
            ? 'This meeting has been submitted and locked in your CRM.'
            : 'This feature is not available in offline mode.' }
        </DNAText>
      </DNAPopover.Content>
    </DNAPopover>
  );
};

export const CRMOpenCallButton = (props: {meetingORM?: MeetingORM}) => {
  const { isOnline } = useAppSettings();
  const tenant = useUserTenant();
  const openInCRM = (): void => {
    const crmCallId = props.meetingORM?.model.crmRecord?.crmCallId;
    const instanceUrl = tenant?.config.crmIntegration?.instanceUrl;
    const isVeevaVault =
      tenant?.config.crmIntegration?.crmIntegrationType === CrmIntegrationType.VEEVA_VAULT;
    if (instanceUrl && crmCallId) {
      analytics?.track('MEETING_CRM_RECORD_OPEN', {
        category: 'MEETING',
        meetingId: props.meetingORM?.model.id,
        crmCallId,
      });
      const url = isVeevaVault
        ? `${instanceUrl}/ui/#app/page/call-report/~/view?id=${crmCallId}`
        : `${instanceUrl}/${crmCallId}`;
      window.open(url, '_blank');
    } else {
      console.error('Instance URL not defined');
    }
  };

  if (!isOnline) return null;

  return (
    <DNAPopover disablePopover={['tabletPWA']}>
      <DNAPopover.Anchor>
        <DNAButton
          testID="open-call-in-crm-button"
          status="tertiary"
          appearance="outline"
          onPress={openInCRM}
        >
          Open in CRM
        </DNAButton>
      </DNAPopover.Anchor>
      <DNAPopover.Content>
        <DNAPopover.Content>
          <DNAText colorMode="dark">
            Open meeting details in CRM in a new window.
          </DNAText>
        </DNAPopover.Content>
      </DNAPopover.Content>
    </DNAPopover>
  );
};

AddMeetingFooter.displayName = 'AddMeetingFooter';

export default AddMeetingFooter;
